<script setup lang="ts">
import { roundToTwoDecimals } from "~/core/utils/number";
import { LicenseGaugeStatus } from "~/modules/license/models/LicenseGaugeStatus.enum";
import type { LicenseResourceUsage } from "~/modules/license/models/LicenseResourceUsage.interface";

const props = defineProps<{
  criteria: LicenseResourceUsage;
  currentNumber: number | undefined;
  licenseGaugeStatus: LicenseGaugeStatus;
  successColor?: string;
}>();

const computedSuccessColor = computed(() => {
  if (props.successColor) {
    return props.successColor;
  }
  return "bg-success-700";
});

const licenseGaugeColor = computed(() => {
  let color: string = computedSuccessColor.value;
  switch (props.licenseGaugeStatus) {
    case LicenseGaugeStatus.OK:
      color = computedSuccessColor.value;
      break;
    case LicenseGaugeStatus.WARNING:
      color = "bg-warning-700";
      break;
    case LicenseGaugeStatus.ERROR:
      color = "bg-error-700";
      break;
  }
  return color;
});

const thresholdColor = computed(() => {
  let color: string = computedSuccessColor.value;
  switch (props.licenseGaugeStatus) {
    case LicenseGaugeStatus.WARNING:
    case LicenseGaugeStatus.ERROR:
      color = "bg-white";
      break;
  }
  return color;
});

const threshold = computed(() => {
  if (
    props.criteria.tolerated &&
    props.criteria.contracted &&
    props.criteria.tolerated > props.criteria.contracted
  ) {
    return (props.criteria.contracted * 100) / props.criteria.tolerated;
  }
  return 0;
});

const currentPercentage = computed(() => {
  return percentageProgressBar(percent(props.criteria) ?? 0);
});

/**
 * Return the ratio about current devices and max number of devices in percent
 * @param criteria
 */
function percent(criteria: LicenseResourceUsage): number | null {
  let percent: number | null = null;
  if (criteria?.contracted && criteria.contracted > 0) {
    const maximum: number =
      criteria.tolerated && criteria.tolerated > criteria.contracted
        ? criteria.tolerated
        : criteria.contracted;
    percent = (props.currentNumber * 100) / maximum;
    percent = roundToTwoDecimals(percent); // round to 2 decimals
  }

  return percent;
}

const percentageProgressBar = (value: number) => {
  return Math.min(value, 100);
};
</script>

<template>
  <div class="flex items-center">
    <div class="relative rounded bg-gray-200 w-full h-2">
      <div
        class="rounded h-2"
        :class="licenseGaugeColor"
        :style="{
          width: `${currentPercentage}%`,
        }"
      />
      <div
        v-if="threshold"
        class="w-[1px] h-full absolute top-0"
        :class="thresholdColor"
        :style="{
          left: threshold + '%',
        }"
      />
    </div>
  </div>
</template>
